import './index.css'
import { useState, useEffect } from 'react'
import { X, Trash2 } from 'lucide-react'
import { maskCurrency, digitsToAmount, fmtAmt } from '../../lib/format'
import { listContributions, addContribution, deleteContribution } from '../../services/goalService'
import type { Goal, GoalContribution } from '../../types'
import { getToastErrorMessage, useToast } from '../Toast'

interface GoalDetailSheetProps {
  open: boolean
  goal: Goal | null
  onClose: () => void
  onContributionAdded: (goalId: string) => void
}

function todayIso(): string {
  return new Date().toISOString().split('T')[0]
}

export default function GoalDetailSheet({ open, goal, onClose, onContributionAdded }: GoalDetailSheetProps) {
  const toast = useToast()
  const [contributions, setContributions] = useState<GoalContribution[]>([])
  const [loading,       setLoading]       = useState(false)

  const [amountDigits, setAmountDigits] = useState('')
  const [date,         setDate]         = useState(todayIso())
  const [note,         setNote]         = useState('')
  const [adding,       setAdding]       = useState(false)
  const [addError,     setAddError]     = useState<string | null>(null)

  useEffect(() => {
    if (!open || !goal) return
    setAmountDigits('')
    setDate(todayIso())
    setNote('')
    setAddError(null)

    let cancelled = false
    setLoading(true)
    listContributions(goal.id)
      .then(data => { if (!cancelled) setContributions(data) })
      .catch(err => {
        console.error('[GoalDetailSheet] fetch error:', err)
        toast.error(getToastErrorMessage(err, 'Erro ao carregar contribuições.'))
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [open, goal])

  if (!open || !goal) return null

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    const amount = digitsToAmount(amountDigits)
    if (amount <= 0) {
      setAddError('Informe um valor')
      toast.warning('Informe um valor.')
      return
    }
    if (!date) {
      setAddError('Informe uma data')
      toast.warning('Informe uma data.')
      return
    }

    setAdding(true)
    setAddError(null)
    try {
      const contribution = await addContribution(goal!.id, amount, date, note.trim() || null)
      setContributions(prev => [contribution, ...prev])
      setAmountDigits('')
      setNote('')
      onContributionAdded(goal!.id)
      toast.success('Contribuição adicionada.')
    } catch (err) {
      const message = getToastErrorMessage(err, 'Erro ao adicionar contribuição.')
      setAddError(message)
      toast.error(message)
    } finally {
      setAdding(false)
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteContribution(id)
      setContributions(prev => prev.filter(c => c.id !== id))
      onContributionAdded(goal!.id)
      toast.success('Contribuição excluída.')
    } catch (err) {
      console.error('[GoalDetailSheet] delete error:', err)
      toast.error(getToastErrorMessage(err, 'Erro ao excluir contribuição.'))
    }
  }

  return (
    <div className="bc-sheet-overlay" onClick={onClose}>
      <div className="bc-sheet" onClick={e => e.stopPropagation()}>
        <div className="bc-sheet__header">
          <span className="bc-sheet__title">
            {goal.emoji && <span style={{ marginRight: 6 }}>{goal.emoji}</span>}
            {goal.name}
          </span>
          <button className="bc-sheet__close" onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </div>

        <div className="bc-sheet__body">
          <form className="bc-goal-detail__add-form" onSubmit={handleAdd}>
            <p className="bc-goal-detail__section-label">Nova contribuição</p>
            <div className="bc-goal-detail__add-row">
              <div className="bc-currency bc-goal-detail__amount">
                <span className="bc-currency__prefix">R$</span>
                <input
                  className="bc-input"
                  type="text"
                  inputMode="numeric"
                  value={maskCurrency(amountDigits)}
                  onChange={e => setAmountDigits(e.target.value.replace(/\D/g, ''))}
                  placeholder="0,00"
                />
              </div>
              <input
                className="bc-input bc-goal-detail__date"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
              />
            </div>
            <input
              className="bc-input"
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Observação (opcional)"
              maxLength={120}
            />
            {addError && <p className="bc-field__error">{addError}</p>}
            <button type="submit" className="bc-btn bc-btn--primary" disabled={adding}>
              {adding ? 'Adicionando…' : 'Adicionar'}
            </button>
          </form>

          <div className="bc-goal-detail__history">
            <p className="bc-goal-detail__section-label">Histórico</p>
            {loading && <p className="bc-loading-text">Carregando…</p>}
            {!loading && contributions.length === 0 && (
              <p className="bc-empty-text">Nenhuma contribuição ainda.</p>
            )}
            {contributions.map(c => (
              <div key={c.id} className="bc-goal-detail__row">
                <div className="bc-goal-detail__row-info">
                  <span className="bc-goal-detail__row-amount">{fmtAmt(c.amount)}</span>
                  <span className="bc-goal-detail__row-date">
                    {new Date(c.date + 'T00:00:00').toLocaleDateString('pt-BR')}
                  </span>
                  {c.note && <span className="bc-goal-detail__row-note">{c.note}</span>}
                </div>
                <button
                  className="bc-icon-btn bc-icon-btn--danger"
                  onClick={() => handleDelete(c.id)}
                  aria-label="Excluir contribuição"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
